<template>
  <div class="multiselect">
    <div class="selected-items-wrapper">
      <button class="non-button-style selected-item-tag" v-for="(item, index) in selected" :key="index" @click.prevent="remove(item)" :disabled="disabledSelectedItems">
        {{ item.display }}
      </button>
    </div>

    <input :aria-label="placeholderText" type="text" v-model="searchItem" :placeholder="placeholderText" @keydown.prevent.enter :autocomplete="autocomplete"/>

    <ul class="option-ul" :disabled="disabled">
      <li
        multiple
        :name="name"
        v-for="(option, index) in options"
        :key="`${uuid}-${index}`"
        :value="option.returnValue"
        v-show="searchItem === '' || RegExp(`${searchItem}`, 'i').test(option.display)"
      >
        <input :id="`${uuid}-${option.display}`" type="checkbox" :disabled="disabled" @change="select(option)" @keyup.enter="select(option)" />
        <label :for="`${uuid}-${option.display}`">{{ option.display }}</label>
      </li>
    </ul>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';

export default {
  name: 'multiSelect',

  props: {
    items: {
      required: true,
      type: Array,
    },
    selectedItems: {
      type: Array,
    },
    id: {
      type: String,
    },
    name: {
      type: String,
    },
    maximum: {
      type: Number,
      default: 5,
    },
    placeholderText: {
      type: String,
    },
    /**
     * Data that is passed to this prop will be emited from this component
     */
    dataToEmitWith: {},

    // Disabled button and then user is not able to click and remove the items
    disabledSelectedItems: {
      type: Boolean,
    },
    autocomplete: {
      type: String,
    },
  },

  data() {
    return {
      options: this.sortOptions(),
      selected: this.selectedItems ? this.selectedItems : [],
      searchItem: '',
      uuid: this.id ? this.id : uuid(),
    };
  },

  computed: {
    disabled() {
      return this.selected.length >= this.maximum;
    },
  },

  watch: {
    selectedItems(newData) {
      this.selected = Array.isArray(newData) ? newData : [];
      this.options = this.sortOptions();
    },
  },

  methods: {
    sortOptions() {
      return this.items
        .filter((i) => {
          const isSelectedItem =
            // Need to support the passed-in `selectedItems` prop being a string AND an object depending on where this component
            // is being used. Note that in NeedsAnalysis.vue, it is a string but in PolicyHolder.vue, it is an object.
            this.selectedItems.includes(i) ||
            this.selectedItems.some((selectedItem) => {
              return selectedItem.display === i.display;
            });
          return isSelectedItem === false;
        })
        .sort((a, b) => {
          const item1 = a.display.toUpperCase();
          const item2 = b.display.toUpperCase();

          return item1.localeCompare(item2);
        });
    },

    select(target) {
      if (!this.disabled) {
        this.selected.push(target);
        this.options = this.options.filter((item) => {
          return item !== target;
        });

        this.options = this.options.sort((a, b) => {
          const item1 = a.display.toUpperCase();
          const item2 = b.display.toUpperCase();

          return item1.localeCompare(item2);
        });

        this.emitData(this.selected);
      }
    },

    remove(target) {
      this.options.push(target);
      this.selected = this.selected.filter((item) => {
        return item !== target;
      });

      this.options = this.options.sort((a, b) => {
        const item1 = a.display.toUpperCase();
        const item2 = b.display.toUpperCase();

        return item1.localeCompare(item2);
      });

      this.emitData(this.selected);
    },

    emitData(data) {
      this.$emit('updateData', data, this.dataToEmitWith);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@general/styles/_constants';
@import '@general/styles/tokens/_index';

.option-ul {
  resize: vertical;
  min-height: 9rem;
  height: 9.5rem;
  max-height: 34rem;
  overflow-y: scroll;
  box-sizing: border-box;
  border: 1px solid $GREY_300;
  color: $GREY_800;
  outline: none;
  transition-property: background, border-color, box-shadow;
  margin-bottom: 0;
  background-color: $WHITE;

  [type='checkbox'] {
    top: 0;

    & + label {
      position: relative;
      padding: $INPUT_BOX_PADDING;
      display: inline-block;
      box-sizing: border-box;
      width: 100%;
      margin: 0;

      &:before,
      &:after {
        display: none;
      }

      &:focus,
      &:hover {
        background-color: $PRIMARY_BRIGHT_GREEN_20;
        font-weight: $BOLD;
      }
    }
  }

  li {
    list-style-type: none;
    overflow-x: hidden;
    position: relative;
  }

  &[disabled]:not([disabled='false']) {
    background: $GREY_300;
    color: $GREY_600;

    label {
      cursor: not-allowed;

      &:checked {
        background: transparent;
      }

      &:hover {
        background-color: $GREY_300;
        font-weight: $NORMAL;
      }
    }
  }
}

.multiselect {
  .selected-items-wrapper {
    padding-bottom: 0.2rem;
  }
}
</style>
